import { useEffect, useState } from 'react'

// หนังสือ — เรียก jos-cowork-api (public ไม่ต้อง login เฉพาะหนังสือที่เผยแพร่แล้ว)
// ที่มาข้อมูลอยู่ใน DB (จัดการที่ backoffice เมนู "หนังสือ") — เลิกใช้ mock data/books.ts แล้ว

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5050/api'

interface ApiResponse<T> {
  success: boolean
  data: T
  error?: string
}

export interface ApiBookCategory {
  category_id: number
  name: string
  slug: string
  book_count: number          // นับเฉพาะเล่มที่เผยแพร่
}

export interface ApiBookListItem {
  book_id: number
  slug: string
  title: string
  category_id: number
  category_name: string | null
  instructor_id: number | null
  instructor_name: string | null
  instructor_avatar_url: string | null
  author_name: string | null
  price: number
  original_price: number | null
  page_count: number | null
  cover_url: string | null
  short_description: string | null
  is_bestseller: boolean
  has_ebook: boolean
  in_stock: boolean           // ไม่ส่งจำนวนสต๊อกจริงให้หน้าเว็บ
  published_at: string | null
}

// เนื้อหาหน้ารายละเอียดแบบแบ่งส่วน (m_book.detail) — ส่วนที่ว่าง = list ว่าง/null (หน้าเว็บซ่อน section เอง)
export interface BookDetailContent {
  problems: string[]
  about: { title: string; text: string }[]
  inclusions: { label: string; tag: string | null }[]
  chapters_title: string | null
  chapters: { no: number; title: string; text: string | null; items: string[] }[]
  audience: { title: string | null; groups: string[] }
}

export interface ApiBookDetail extends ApiBookListItem {
  images: string[] | null
  detail: BookDetailContent | null
}

export type BookSort = 'newest' | 'popular' | 'price-asc' | 'price-desc'

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`)
  let json: ApiResponse<T> | null = null
  try {
    json = await res.json()
  } catch {
    /* ตอบกลับไม่ใช่ JSON */
  }
  if (!json || !json.success) throw new Error(json?.error || 'โหลดข้อมูลหนังสือไม่สำเร็จ')
  return json.data
}

export const getBookCategories = () => get<ApiBookCategory[]>('/book-categories')

export const getBooks = (params: { category_id?: number | null; search?: string; sort?: BookSort } = {}) => {
  const query = new URLSearchParams()
  if (params.category_id) query.append('category_id', String(params.category_id))
  if (params.search?.trim()) query.append('search', params.search.trim())
  if (params.sort) query.append('sort', params.sort)
  const qs = query.toString()
  return get<ApiBookListItem[]>(`/books${qs ? `?${qs}` : ''}`)
}

export const getBookBySlug = (slug: string) => get<ApiBookDetail>(`/books/${encodeURIComponent(slug)}`)

// ราคาก่อนลด → % ส่วนลด (null = ไม่มีส่วนลด)
export const bookDiscountPct = (b: Pick<ApiBookListItem, 'price' | 'original_price'>) =>
  b.original_price && b.original_price > b.price ? Math.round((1 - b.price / b.original_price) * 100) : null

// ── หมวดหมู่หนังสือสำหรับเมนู (Navbar/Footer) — ดึงครั้งเดียวต่อการโหลดหน้า แชร์ผลกันทุกคอมโพเนนต์ ──
let categoriesPromise: Promise<ApiBookCategory[]> | null = null

export function useBookCategories(): ApiBookCategory[] {
  const [categories, setCategories] = useState<ApiBookCategory[]>([])
  useEffect(() => {
    let cancelled = false
    if (!categoriesPromise) {
      categoriesPromise = getBookCategories().catch(() => {
        categoriesPromise = null   // ล้มเหลวแล้วอย่าจำ — ให้ลองใหม่ได้
        return []
      })
    }
    categoriesPromise.then((rows) => { if (!cancelled) setCategories(rows) })
    return () => { cancelled = true }
  }, [])
  return categories
}
